import React, { useState } from "react";
import { useIntl } from "react-intl";
import FiltersSkeleton from "../Skeletons/FiltersSkeleton";
import {
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    TextField,
    InputAdornment,
} from "@mui/material";
import { HiChevronDown, HiMagnifyingGlass } from "react-icons/hi2";
import {
    initial_subCategories,
    blankStatusFilters,
} from "../../utils/Clients/mapping";

interface FiltersProps {
    subCategories: Record<string, string[]>;
    expandedLevels: Record<string, boolean>;
    setExpandedLevels: React.Dispatch<
        React.SetStateAction<Record<string, boolean>>
    >;
    selectedFilters: Record<string, string[]>;
    setSelectedFilters: React.Dispatch<
        React.SetStateAction<Record<string, string[]>>
    >;
    reset: () => void;
    statusFilters?: Record<string, boolean[]> | null;
    setStatusFilters?: React.Dispatch<
        React.SetStateAction<Record<string, boolean[]>>
    > | null;
    arrayFilters?: (string | number)[] | null;
    setArrayFilters?: React.Dispatch<
        React.SetStateAction<number[] | string[] | null>
    >;
    mobileFilterOpen?: boolean;
    mobileFilters?: Record<string, string[]>;
    setMobileFilters?: React.Dispatch<
        React.SetStateAction<Record<string, string[]>>
    >;
    mobileStatusFilters?: Record<string, boolean[]> | null;
    setMobileStatusFilters?: React.Dispatch<
        React.SetStateAction<Record<string, boolean[]>>
    > | null;
    mobileArrayFilters?: (string | number)[];
    setMobileArrayFilters?: React.Dispatch<
        React.SetStateAction<number[] | string[] | null>
    >;
    isFilterOpen?: boolean;
    setIsFilterOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Filters: React.FC<FiltersProps> = ({
    subCategories,
    expandedLevels,
    setExpandedLevels,
    selectedFilters,
    setSelectedFilters,
    reset,
    statusFilters,
    setStatusFilters,
    arrayFilters,
    setArrayFilters,
    mobileFilterOpen,
    mobileFilters,
    setMobileFilters,
    mobileStatusFilters,
    setMobileStatusFilters,
    mobileArrayFilters,
    setMobileArrayFilters,
    setIsFilterOpen,
}) => {
    const intl = useIntl();
    const [citySearchTerm, setCitySearchTerm] = useState("");
    const [expandedFilters, setExpandedFilters] = useState<
        Record<string, boolean>
    >({});

    const handleAccordionChange =
        (level: string) =>
        (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpandedLevels((prev) => ({
                ...prev,
                [level]: isExpanded,
            }));
        };

    const handleFilterChange = (key: string, value: string) => {
        const setFilters = mobileFilterOpen
            ? setMobileFilters
            : setSelectedFilters;

        if (!setFilters) return;

        setFilters((prev) => {
            const currentValues = prev[key] || [];
            const newValues = currentValues.includes(value)
                ? currentValues.filter((v) => v !== value)
                : [...currentValues, value];
            return { ...prev, [key]: newValues };
        });

        if (key === "city") {
            setCitySearchTerm("");
        }
    };

    const handleStatusFilterChange = (filter: string, value: boolean) => {
        const setFilters = mobileFilterOpen
            ? setMobileStatusFilters
            : setStatusFilters;
        if (!setFilters) return;

        setFilters((prev) => {
            const currentValues = prev[filter] || [];
            const newValues = currentValues.includes(value)
                ? currentValues.filter((v) => v !== value)
                : [...currentValues, value];
            return { ...prev, [filter]: newValues };
        });
    };

    const handleArrayFilterChange = (value: number | string) => {
        const setFilters = mobileFilterOpen
            ? setMobileArrayFilters
            : setArrayFilters;
        if (!setFilters) return;

        if (
            (mobileFilterOpen ? mobileArrayFilters : arrayFilters)?.includes(
                value
            )
        ) {
            setFilters((prev) => {
                if (Array.isArray(prev)) {
                    const filtered = prev.filter((day) => day !== value);
                    return typeof value === "number"
                        ? (filtered as number[])
                        : (filtered as string[]);
                }
                return typeof value === "number"
                    ? ([] as number[])
                    : ([] as string[]);
            });
        } else {
            setFilters((prev) => {
                if (Array.isArray(prev)) {
                    const newArray = [...prev, value];
                    return typeof value === "number"
                        ? (newArray as number[])
                        : (newArray as string[]);
                }
                return typeof value === "number"
                    ? ([value] as number[])
                    : ([value] as string[]);
            });
        }
    };

    const applyFilters = () => {
        if (setMobileFilters && setSelectedFilters && setIsFilterOpen) {
            setSelectedFilters(mobileFilters || {});
            if (setMobileStatusFilters && setStatusFilters) {
                setStatusFilters(mobileStatusFilters || {});
            }
            if (setMobileArrayFilters && setArrayFilters) {
                setArrayFilters(
                    (mobileArrayFilters || []) as number[] | string[]
                );
            }
            setIsFilterOpen(false);
        }
    };

    const handleCitySearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCitySearchTerm(event.target.value);
    };

    const getFilteredAndSortedCities = (cities: string[]) => {
        const searchTerm = citySearchTerm.toLowerCase();
        const selectedCities = mobileFilterOpen
            ? mobileFilters?.["city"] || []
            : selectedFilters["city"] || [];

        // First, filter cities based on search term
        const filteredCities = cities.filter((city) =>
            city.toLowerCase().includes(searchTerm)
        );

        // Then, sort cities with selected ones at the top, then alphabetically
        return filteredCities.sort((a, b) => {
            const aSelected = selectedCities.includes(a);
            const bSelected = selectedCities.includes(b);

            // First sort by selection status
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;

            // Then sort alphabetically
            const labelA = intl.formatMessage({ id: a }).toLowerCase();
            const labelB = intl.formatMessage({ id: b }).toLowerCase();
            return labelA.localeCompare(labelB);
        });
    };

    const toggleFilterExpansion = (key: string) => {
        setExpandedFilters((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const renderFilterItems = (key: string, items: string[]) => {
        const isExpanded = expandedFilters[key];
        const displayItems = isExpanded ? items : items.slice(0, 5);
        const hasMore = items.length > 5;

        return (
            <>
                {displayItems.map((el, index) => (
                    <FormGroup key={index} sx={{ marginLeft: "8px" }}>
                        <div className="cursor-pointer flex mb-1">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        color="secondary"
                                        checked={
                                            arrayFilters && key === "visit_days"
                                                ? (mobileFilterOpen
                                                      ? mobileArrayFilters
                                                      : arrayFilters
                                                  ).includes(el)
                                                : (mobileFilterOpen
                                                      ? mobileFilters
                                                      : selectedFilters)[
                                                      key
                                                  ]?.includes(el)
                                        }
                                        onChange={() =>
                                            arrayFilters && key === "visit_days"
                                                ? handleArrayFilterChange(el)
                                                : handleFilterChange(key, el)
                                        }
                                    />
                                }
                                label={
                                    typeof el === "boolean" ||
                                    el === "true" ||
                                    el === "false"
                                        ? el === "true"
                                            ? intl.formatMessage({ id: "yes" })
                                            : intl.formatMessage({ id: "no" })
                                        : intl.formatMessage({ id: String(el) })
                                }
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: {
                                            xs: "1.1rem",
                                            md: "0.9rem",
                                        },
                                        maxWidth: "200px",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        marginLeft: "4px",
                                    },
                                }}
                            />
                        </div>
                    </FormGroup>
                ))}
                {hasMore && (
                    <Button
                        size="small"
                        color="secondary"
                        onClick={() => toggleFilterExpansion(key)}
                        sx={{
                            fontSize: {
                                xs: "0.9rem",
                                md: "0.8rem",
                            },
                            textTransform: "none",
                            padding: "4px 8px",
                            minWidth: "auto",
                            marginLeft: "8px",
                        }}
                    >
                        {isExpanded
                            ? intl.formatMessage({ id: "see_less" })
                            : intl.formatMessage({ id: "see_more" })}
                    </Button>
                )}
            </>
        );
    };

    return (
        <div className="flex flex-col h-full w-full relative">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 pb-4 no-scrollbar">
                {JSON.stringify(subCategories) !==
                JSON.stringify(initial_subCategories) ? (
                    <div className="flex flex-col w-full mt-6 md:mt-4 gap-3">
                        {/* Status Filters */}
                        {statusFilters && setStatusFilters && (
                            <Accordion
                                expanded={expandedLevels?.["status"] || false}
                                onChange={handleAccordionChange("status")}
                                sx={{
                                    boxShadow: "none",
                                    "&:before": { display: "none" },
                                    "&.Mui-expanded": {
                                        marginBottom: 0,
                                        marginTop: 0,
                                    },
                                    maxWidth: {
                                        xs: "100%",
                                        md: "250px",
                                    },
                                    width: "100%",
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<HiChevronDown />}
                                    sx={{
                                        padding: "0px",
                                        minHeight: "unset !important",
                                        "& .MuiAccordionSummary-content": {
                                            margin: "4px 0",
                                            "&.Mui-expanded": {
                                                margin: "4px 0",
                                            },
                                        },
                                        "&.Mui-expanded": {
                                            minHeight: "unset !important",
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 550,
                                            fontSize: {
                                                xs: "1.15rem",
                                                md: "0.91rem",
                                            },
                                        }}
                                    >
                                        {intl.formatMessage({ id: "status" })}
                                        {(mobileFilterOpen
                                            ? mobileStatusFilters
                                            : statusFilters) &&
                                            Object.values(
                                                mobileFilterOpen
                                                    ? mobileStatusFilters
                                                    : statusFilters
                                            ).some((arr) => arr.length > 0) && (
                                                <span className="ml-1">
                                                    (
                                                    {Object.values(
                                                        mobileFilterOpen
                                                            ? mobileStatusFilters
                                                            : statusFilters
                                                    ).reduce(
                                                        (acc, curr) =>
                                                            acc + curr.length,
                                                        0
                                                    )}
                                                    )
                                                </span>
                                            )}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: "0px" }}>
                                    {Object.keys(
                                        mobileFilterOpen
                                            ? mobileStatusFilters
                                            : statusFilters
                                    ).map((filter) => (
                                        <FormGroup key={filter}>
                                            <div className="text-left text-lg md:text-base">
                                                {intl.formatMessage({
                                                    id: filter,
                                                })}
                                            </div>
                                            <div className="flex flex-col gap-1 pl-2 mb-1">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            color="secondary"
                                                            checked={(mobileFilterOpen
                                                                ? mobileStatusFilters
                                                                : statusFilters)[
                                                                filter
                                                            ]?.includes(true)}
                                                            onChange={() =>
                                                                handleStatusFilterChange(
                                                                    filter,
                                                                    true
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label={intl.formatMessage({
                                                        id: "yes",
                                                    })}
                                                    sx={{
                                                        "& .MuiFormControlLabel-label":
                                                            {
                                                                fontSize: {
                                                                    xs: "1.1rem",
                                                                    md: "0.9rem",
                                                                },
                                                                marginLeft:
                                                                    "4px",
                                                            },
                                                    }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            color="secondary"
                                                            checked={(mobileFilterOpen
                                                                ? mobileStatusFilters
                                                                : statusFilters)[
                                                                filter
                                                            ]?.includes(false)}
                                                            onChange={() =>
                                                                handleStatusFilterChange(
                                                                    filter,
                                                                    false
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label={intl.formatMessage({
                                                        id: "no",
                                                    })}
                                                    sx={{
                                                        "& .MuiFormControlLabel-label":
                                                            {
                                                                fontSize: {
                                                                    xs: "1.1rem",
                                                                    md: "0.9rem",
                                                                },
                                                                marginLeft:
                                                                    "4px",
                                                            },
                                                    }}
                                                />
                                            </div>
                                        </FormGroup>
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* Regular Filters */}
                        {Object.entries(subCategories || {})
                            .filter(([, value]) => value.length > 0)
                            .map(([key, value]) => (
                                <Accordion
                                    key={key}
                                    expanded={expandedLevels?.[key] || false}
                                    onChange={handleAccordionChange(key)}
                                    sx={{
                                        boxShadow: "none",
                                        "&:before": { display: "none" },
                                        "&.Mui-expanded": {
                                            marginBottom: 0,
                                            marginTop: 0,
                                        },
                                        maxWidth: {
                                            xs: "100%",
                                            md: "250px",
                                        },
                                        width: "100%",
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<HiChevronDown />}
                                        sx={{
                                            padding: "0px",
                                            minHeight: "unset !important",
                                            "& .MuiAccordionSummary-content": {
                                                margin: "4px 0",
                                                "&.Mui-expanded": {
                                                    margin: "4px 0",
                                                },
                                            },
                                            "&.Mui-expanded": {
                                                minHeight: "unset !important",
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 550,
                                                fontSize: {
                                                    xs: "1.15rem",
                                                    md: "0.91rem",
                                                },
                                            }}
                                        >
                                            {intl.formatMessage({ id: key })}
                                            {mobileFilterOpen
                                                ? mobileFilters?.[key]?.length >
                                                      0 && (
                                                      <span className="ml-1">
                                                          (
                                                          {
                                                              mobileFilters[key]
                                                                  .length
                                                          }
                                                          )
                                                      </span>
                                                  )
                                                : selectedFilters[key]?.length >
                                                      0 && (
                                                      <span className="ml-1">
                                                          (
                                                          {
                                                              selectedFilters[
                                                                  key
                                                              ].length
                                                          }
                                                          )
                                                      </span>
                                                  )}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ padding: "0px" }}>
                                        {key === "city" && (
                                            <div
                                                className="mb-2"
                                                style={{ marginLeft: "0px" }}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={citySearchTerm}
                                                    onChange={handleCitySearch}
                                                    placeholder={intl.formatMessage(
                                                        { id: "search" }
                                                    )}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <HiMagnifyingGlass
                                                                    className="text-gray-500"
                                                                    size={18}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        "& .MuiInputBase-input":
                                                            {
                                                                fontSize:
                                                                    "16px",
                                                            },
                                                        "& .MuiInputBase-root":
                                                            {
                                                                backgroundColor:
                                                                    "#f3f4f6",
                                                                borderRadius:
                                                                    "9999px",
                                                                height: "40px",
                                                            },
                                                        "& .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                border: "none",
                                                            },
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {Array.isArray(value) &&
                                            renderFilterItems(
                                                key,
                                                key === "city"
                                                    ? getFilteredAndSortedCities(
                                                          value
                                                      )
                                                    : value
                                            )}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                    </div>
                ) : (
                    <FiltersSkeleton />
                )}
                {/* Mobile Spacer */}
                <div className="h-32 md:hidden w-full" />
            </div>

            {/* Footer */}
            {((!mobileFilterOpen &&
                ((selectedFilters &&
                    Object.keys(selectedFilters).some(
                        (key) => selectedFilters[key].length > 0
                    )) ||
                    (statusFilters &&
                        JSON.stringify(statusFilters) !==
                            JSON.stringify(blankStatusFilters)))) ||
                (mobileFilterOpen &&
                    ((mobileFilters &&
                        Object.keys(mobileFilters).some(
                            (key) => mobileFilters[key].length > 0
                        )) ||
                        (mobileStatusFilters &&
                            JSON.stringify(mobileStatusFilters) !==
                                JSON.stringify(blankStatusFilters))))) && (
                <div
                    className={`mt-auto bg-white w-full flex justify-center gap-3 ${mobileFilterOpen ? "fixed" : "relative"} bottom-0 left-0 md:left-auto p-3 md:p-0 md:block border-t md:border-0 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] md:shadow-none z-10`}
                >
                    <Button
                        size="small"
                        color="secondary"
                        onClick={reset}
                        sx={{
                            fontSize: {
                                xs: "1rem",
                                md: "0.875rem",
                            },
                        }}
                    >
                        {intl.formatMessage({ id: "filters.reset" })}
                    </Button>
                    {mobileFilterOpen && (
                        <div className="block md:hidden">
                            <Button
                                size="small"
                                color="secondary"
                                variant="contained"
                                onClick={applyFilters}
                                sx={{
                                    fontSize: {
                                        xs: "1rem",
                                        md: "0.875rem",
                                    },
                                }}
                            >
                                {intl.formatMessage({
                                    id: "filters.apply",
                                })}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Filters;
